import {
  AiAuditProblemSeverity,
  AiAuditReport,
  AiAuditVerdict
} from "@domain/ai_answers/audit_report"
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined"
import CancelIcon from "@mui/icons-material/Cancel"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import { Box, Chip, Typography } from "@mui/material"

const MONO =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace"

type VerdictStyle = {
  label: string
  color: string
  bg: string
  border: string
  Icon: typeof CheckCircleIcon
}

const VERDICT_STYLES: Record<AiAuditVerdict, VerdictStyle> = {
  CORRECT: {
    label: "DOĞRU",
    color: "#1b5e20",
    bg: "rgba(46, 125, 50, 0.08)",
    border: "rgba(46, 125, 50, 0.35)",
    Icon: CheckCircleIcon
  },
  PARTIALLY_CORRECT: {
    label: "KISMEN DOĞRU",
    color: "#8a6d00",
    bg: "rgba(240, 196, 25, 0.14)",
    border: "rgba(240, 196, 25, 0.5)",
    Icon: WarningAmberIcon
  },
  WRONG: {
    label: "YANLIŞ",
    color: "#b3261e",
    bg: "rgba(211, 47, 47, 0.08)",
    border: "rgba(211, 47, 47, 0.35)",
    Icon: CancelIcon
  },
  UNDETERMINED: {
    label: "BELİRSİZ",
    color: "#455a64",
    bg: "rgba(69, 90, 100, 0.08)",
    border: "rgba(69, 90, 100, 0.3)",
    Icon: HelpOutlineIcon
  }
}

const CONFIDENCE_LABELS: Record<string, string> = {
  HIGH: "yüksek güven",
  MEDIUM: "orta güven",
  LOW: "düşük güven"
}

const SEVERITY_STYLES: Record<
  AiAuditProblemSeverity,
  { color: string; Icon: typeof ErrorOutlineIcon }
> = {
  BLOCKER: { color: "#b3261e", Icon: ErrorOutlineIcon },
  WARNING: { color: "#8a6d00", Icon: WarningAmberIcon },
  INFO: { color: "#455a64", Icon: InfoOutlinedIcon }
}

function Section({
  title,
  children
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          mb: 0.75,
          textTransform: "uppercase",
          fontSize: 10,
          letterSpacing: 0.6,
          fontWeight: 700
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  )
}

/**
 * Verdict first, evidence second. The previous renderer showed a markdown essay whose
 * "is this right?" line was buried four sections deep.
 */
export default function AiVerdictReport({
  report,
  question
}: {
  report: AiAuditReport
  question?: string | null
}) {
  const style = VERDICT_STYLES[report.verdict]
  const { Icon } = style

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          p: 1.5,
          borderRadius: 1.5,
          border: 1,
          borderColor: style.border,
          bgcolor: style.bg
        }}
      >
        <Icon
          sx={{ color: style.color, fontSize: 26, flexShrink: 0, mt: 0.1 }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              mb: 0.25
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: style.color, fontWeight: 800, letterSpacing: 0.4 }}
            >
              {style.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {CONFIDENCE_LABELS[report.confidence] ?? report.confidence}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, lineHeight: 1.45 }}
          >
            {report.headline}
          </Typography>
          {question && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.75 }}
            >
              Soru: {question}
            </Typography>
          )}
        </Box>
      </Box>

      {report.decision && (
        <Section title="Ne yapıldı">
          <Typography variant="body2">{report.decision}</Typography>
        </Section>
      )}

      {report.because.length > 0 && (
        <Section title="Neden">
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            {report.because.map((line) => (
              <Typography
                key={line}
                component="li"
                variant="body2"
                sx={{ mb: 0.5 }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        </Section>
      )}

      {report.evidence.length > 0 && (
        <Section title="Kanıt">
          {report.evidence.map((item) => (
            <Box
              key={`${item.source}-${item.quote}`}
              sx={{
                mb: 1,
                pl: 1.25,
                borderLeft: 3,
                borderColor: "divider"
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontStyle: "italic",
                  color: "text.primary",
                  wordBreak: "break-word"
                }}
              >
                “{item.quote}”
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mt: 0.375,
                  flexWrap: "wrap"
                }}
              >
                <Chip
                  size="small"
                  label={item.source}
                  variant="outlined"
                  sx={{ height: 18, fontSize: 10, fontFamily: MONO }}
                />
                {item.impact && (
                  <Typography variant="caption" color="text.secondary">
                    {item.impact}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Section>
      )}

      {report.problems.length > 0 && (
        <Section title="Sorunlar">
          {report.problems.map((problem) => {
            const severity = SEVERITY_STYLES[problem.severity]
            const SeverityIcon = severity.Icon
            return (
              <Box
                key={problem.detail}
                sx={{ display: "flex", gap: 0.75, mb: 0.75 }}
              >
                <SeverityIcon
                  sx={{
                    fontSize: 16,
                    color: severity.color,
                    flexShrink: 0,
                    mt: 0.25
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2">{problem.detail}</Typography>
                  {problem.rootStep && (
                    <Chip
                      size="small"
                      label={`kök adım · ${problem.rootStep}`}
                      variant="outlined"
                      sx={{
                        mt: 0.375,
                        height: 18,
                        fontSize: 10,
                        fontFamily: MONO
                      }}
                    />
                  )}
                </Box>
              </Box>
            )
          })}
        </Section>
      )}

      {report.fix && (
        <Section title="Düzeltme önerisi">
          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              p: 1.25,
              borderRadius: 1,
              bgcolor: "action.hover"
            }}
          >
            <BuildOutlinedIcon
              sx={{
                fontSize: 16,
                color: "text.secondary",
                flexShrink: 0,
                mt: 0.25
              }}
            />
            <Typography variant="body2">{report.fix}</Typography>
          </Box>
        </Section>
      )}
    </Box>
  )
}
